/**
 * Conversation Store
 *
 * Manages conversation/chat state
 */

import { create } from 'zustand';

export interface Conversation {
  id: string;
  type: 'chat' | 'form' | 'workflow' | 'mixed';
  title?: string;
  participants: string[];
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface ConversationStore {
  conversations: Conversation[];
  activeConversationId: string | null;
  isLoading: boolean;
  error: string | null;
  addConversation: (conversation: Conversation) => void;
  removeConversation: (id: string) => void;
  setActiveConversation: (id: string | null) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  getConversation: (id: string) => Conversation | undefined;
}

export const useConversationStore = create<ConversationStore>((set, get) => ({
  conversations: [],
  activeConversationId: null,
  isLoading: false,
  error: null,

  addConversation: (conversation: Conversation) =>
    set((state) => ({
      conversations: [...state.conversations, conversation],
      error: null,
    })),

  removeConversation: (id: string) =>
    set((state) => ({
      conversations: state.conversations.filter((c) => c.id !== id),
    })),

  setActiveConversation: (id: string | null) =>
    set({ activeConversationId: id }),

  updateConversation: (id: string, updates: Partial<Conversation>) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    })),

  setLoading: (isLoading: boolean) => set({ isLoading }),

  setError: (error: string | null) => set({ error }),

  getConversation: (id: string) => {
    const state = get();
    return state.conversations.find((c) => c.id === id);
  },
}));
